import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Alert } from '../../components/Overlay.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { leaveGroup, patchGroup, useSheets } from '../../api/rooms.ts';
import type { Group, SheetBrief } from '../../domain.ts';
import { MemberPopup } from './MemberPopup.tsx';
import { OptionMenu } from './OptionMenu.tsx';

/**
 * F3 모임 기록지 리스트 — 그리고 그 위에 뜨는 것들.
 *
 *   F3-1     ⋯ 모임 메뉴 (그룹장은 수정 · 나가기, 그룹원은 나가기)
 *   F3-1-2   나가기 확인 — 그룹원
 *   F3-1-3   나가기 확인 — 그룹장. 넘길 사람을 함께 고릅니다
 *   F3-2     구성원 창. 제목 아래 얼굴 줄을 누르면 열립니다
 *
 * 카드에는 본문 대신 **그 날 담긴 항목 구성**을 한 줄 요약으로 적습니다 —
 * 어떤 모임이었는지 목록에서 읽힙니다. 요약은 서버가 만들어 보내고, 숫자는
 * 줄의 개수가 아니라 **적힌 줄의 개수**입니다.
 */

/** 지금 무엇이 떠 있는가 */
type Over = 'none' | 'menu' | 'members' | 'leave';

interface Props {
  group: Group;
  onBack: () => void;
  onOpenSheet: (sheetId: string) => void;
  onNewSheet: () => void;
  onEditGroup: () => void;
  /** 나간 뒤에는 모임 목록(F1)으로 돌아갑니다 */
  onLeft: () => void;
  /** 구성원이 바뀌면 부모가 모임을 다시 읽습니다 */
  onChanged: () => void;
}

export function SheetList({
  group,
  onBack,
  onOpenSheet,
  onNewSheet,
  onEditGroup,
  onLeft,
  onChanged,
}: Props) {
  const { data: sheets, loading, error, reload } = useSheets(group.groupId);
  const [over, setOver] = useState<Over>('none');
  const [failure, setFailure] = useState<string | null>(null);

  const list = sheets ?? [];
  const owner = group.members.find((m) => m.userId === group.ownerId);

  const apply = async (run: () => Promise<unknown>) => {
    setFailure(null);
    try {
      await run();
      onChanged();
      reload();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '바꾸지 못했습니다.');
    }
  };

  return (
    <div className="screen">
      <NavBar
        title={group.name}
        subtitle={`구성원 ${group.members.length}`}
        start={{ icon: 'back', label: '모임 목록', onClick: onBack }}
        end={{
          icon: 'more',
          label: '모임 메뉴',
          pressed: over === 'menu',
          onClick: () => setOver('menu'),
        }}
      />

      <div className="screen__body">
        {/* 얼굴 줄을 누르면 구성원 창(F3-2)이 기록지 리스트 위로 뜹니다 */}
        <button type="button" className="facepile" onClick={() => setOver('members')}>
          {group.members.slice(0, 5).map((m) => (
            <Avatar key={m.userId} name={m.name ?? '?'} seed={m.userId} />
          ))}
          {group.members.length > 5 && (
            <span className="facepile__more">+{group.members.length - 5}</span>
          )}
          <span className="facepile__owner">그룹장 · {owner?.name ?? '이름 없음'}</span>
        </button>

        {loading && <Loading label="기록지를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        <p className="section-title">
          모임 기록지 <span>{list.length}</span>
        </p>

        {!loading && list.length === 0 ? (
          <div className="empty">
            <p className="empty__title">아직 기록지가 없습니다</p>
            <p className="empty__hint">
              {group.canManage
                ? '오른쪽 아래 ＋ 로 첫 기록지를 만들어 보세요.'
                : '그룹장이 기록지를 만들면 여기 쌓입니다.'}
            </p>
          </div>
        ) : (
          <div className="stack">
            {list.map((s, at) => (
              <SheetCard key={s.sheetId} sheet={s} latest={at === 0} onOpen={onOpenSheet} />
            ))}
          </div>
        )}
      </div>

      {/* 기록지 만들기는 리스트 전체에 대한 동작이라 FAB — 그룹장만 */}
      {group.canManage && (
        <button type="button" className="fab" aria-label="기록지 만들기" onClick={onNewSheet}>
          <Icon name="plus" size={23} />
        </button>
      )}

      {over === 'menu' && (
        <OptionMenu
          onClose={() => setOver('none')}
          options={[
            // 권한에 따라 보이는 버튼이 다릅니다 (F3-1)
            ...(group.canManage
              ? [{ icon: 'pencil' as const, label: '모임 수정', onClick: onEditGroup }]
              : []),
            { icon: 'exit' as const, label: '나가기', crit: true, onClick: () => setOver('leave') },
          ]}
        />
      )}

      {over === 'members' && (
        <MemberPopup
          group={group}
          onClose={() => setOver('none')}
          onAdd={() => {
            setOver('none');
            onEditGroup();
          }}
          onSave={(members) => void apply(() => patchGroup(group.groupId, { members }))}
        />
      )}

      {over === 'leave' && (
        <LeaveConfirm
          group={group}
          onClose={() => setOver('none')}
          onLeave={async (nextOwner) => {
            setFailure(null);
            try {
              await leaveGroup(group.groupId, nextOwner);
              onLeft();
            } catch (e) {
              setOver('none');
              setFailure(e instanceof ApiError ? e.message : '나가지 못했습니다.');
            }
          }}
        />
      )}
    </div>
  );
}

function SheetCard({
  sheet,
  latest,
  onOpen,
}: {
  sheet: SheetBrief;
  latest: boolean;
  onOpen: (sheetId: string) => void;
}) {
  const date = sheet.metOn.slice(5).replace('-', '.');
  return (
    <button type="button" className="card" onClick={() => onOpen(sheet.sheetId)}>
      <span className="card__pills card__pills--between">
        <span className="card__title">{sheet.title}</span>
        <span className="card__meta">
          {latest && <span className="pill pill--status" data-status="IN_PROGRESS">최신</span>}
          {date}
        </span>
      </span>
      {/* 본문 대신 그 날 담긴 항목 구성 — 어떤 모임이었는지가 목록에서 읽힙니다 */}
      {sheet.summary !== '' && <span className="card__text">{sheet.summary}</span>}
    </button>
  );
}

/**
 * F3-1-2 · F3-1-3 — 나가기 확인.
 *
 * 그룹원은 확인만, **그룹장은 넘길 사람을 함께 고릅니다.** 고르기 전에는
 * 「넘기고 나가기」가 눌리지 않습니다 — 넘기지 않으면 모임에 주인이
 * 없어지므로, 고르는 것이 나가기의 전제 조건입니다.
 */
function LeaveConfirm({
  group,
  onClose,
  onLeave,
}: {
  group: Group;
  onClose: () => void;
  onLeave: (nextOwner?: string) => void;
}) {
  const [nextOwner, setNextOwner] = useState<string | null>(null);
  const others = group.members.filter((m) => m.userId !== group.ownerId);

  if (!group.canManage) {
    return (
      <Alert
        title="모임을 나갈까요?"
        onClose={onClose}
        actions={
          <>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              취소
            </button>
            <button type="button" className="btn btn--danger" onClick={() => onLeave()}>
              나가기
            </button>
          </>
        }
      >
        {/* 나가면 무엇이 달라지는지 한 문장씩 */}
        <p>이 모임의 기록지가 더 이상 보이지 않습니다.</p>
        <p className="alert__note">
          다시 들어오려면 그룹장이 구성원으로 추가해야 합니다. 내 Prayer Book 에 담아 둔 기도
          노트는 그대로 남습니다.
        </p>
      </Alert>
    );
  }

  return (
    <Alert
      title="그룹장을 넘기고 나갑니다"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={nextOwner === null}
            onClick={() => nextOwner !== null && onLeave(nextOwner)}
          >
            넘기고 나가기
          </button>
        </>
      }
    >
      {others.length === 0 ? (
        <p>
          구성원이 나뿐이라 넘길 사람이 없습니다. 먼저 구성원을 추가하거나, 모임을 그대로 두세요.
        </p>
      ) : (
        <>
          <p>넘겨받은 사람이 새 그룹장이 됩니다.</p>
          <div className="stack stack--tight">
            {others.map((m) => {
              const on = m.userId === nextOwner;
              return (
                <button
                  key={m.userId}
                  type="button"
                  className="pickrow"
                  aria-pressed={on}
                  onClick={() => setNextOwner(m.userId)}
                >
                  <Avatar name={m.name ?? '?'} seed={m.userId} />
                  <span className="pickrow__name">{m.name ?? '이름 없음'}</span>
                  <span className={on ? 'chk chk--on' : 'chk'}>
                    {on && <Icon name="check" size={12} />}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </Alert>
  );
}
